(function (window, document) {
  "use strict";

  var CONSENT_VERSION = "2026-05-05";
  var STORAGE_KEY = "aiplaces.cookieConsent";
  var COOKIE_KEY = "ap_cookie_consent";
  var OPTIONAL_CATEGORIES = ["analytics", "marketing", "preferences"];
  var ALL_CATEGORIES = ["necessary"].concat(OPTIONAL_CATEGORIES);
  var listeners = [];
  var integrations = {
    gtm: false
  };

  var config = {
    dataLayerName: "dataLayer",
    gtmId: "",
    cookieMaxAgeDays: 180
  };

  if (window.AiPlacesConsentConfig && typeof window.AiPlacesConsentConfig === "object") {
    mergeConfig(window.AiPlacesConsentConfig);
  }

  function mergeConfig(nextConfig) {
    Object.keys(nextConfig || {}).forEach(function (key) {
      if (typeof nextConfig[key] !== "undefined" && nextConfig[key] !== null) {
        config[key] = nextConfig[key];
      }
    });
  }

  function parseJson(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function readLocalStorage() {
    try {
      return parseJson(window.localStorage.getItem(STORAGE_KEY) || "");
    } catch (error) {
      return null;
    }
  }

  function writeLocalStorage(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Consent must continue to work even when storage is blocked.
    }
  }

  function removeLocalStorage() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // No-op.
    }
  }

  function readCookie() {
    var match = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_KEY + "=([^;]*)"));
    if (!match) {
      return null;
    }

    try {
      return parseJson(decodeURIComponent(match[1]));
    } catch (error) {
      return null;
    }
  }

  function writeCookie(state) {
    var maxAge = Math.max(1, Number(config.cookieMaxAgeDays) || 180) * 24 * 60 * 60;
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = COOKIE_KEY + "=" + encodeURIComponent(JSON.stringify(state)) +
      "; Max-Age=" + maxAge + "; Path=/; SameSite=Lax" + secure;
  }

  function removeCookie(name, domain) {
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    var domainPart = domain ? "; Domain=" + domain : "";
    document.cookie = name + "=; Max-Age=0; Path=/" + domainPart + "; SameSite=Lax" + secure;
  }

  function clearConsentCookie() {
    removeCookie(COOKIE_KEY);
    getDomainCandidates().forEach(function (domain) {
      removeCookie(COOKIE_KEY, domain);
    });
  }

  function normalizePreferences(preferences) {
    var normalized = {
      necessary: true
    };

    OPTIONAL_CATEGORIES.forEach(function (category) {
      normalized[category] = Boolean(preferences && preferences[category]);
    });

    return normalized;
  }

  function normalizeState(rawState) {
    if (!rawState || typeof rawState !== "object") {
      return null;
    }

    if (rawState.version !== CONSENT_VERSION) {
      return null;
    }

    var preferences = normalizePreferences(rawState.categories || rawState);

    return {
      version: CONSENT_VERSION,
      categories: preferences,
      updatedAt: typeof rawState.updatedAt === "string" ? rawState.updatedAt : "",
      source: typeof rawState.source === "string" ? rawState.source : "stored"
    };
  }

  function getState() {
    return normalizeState(readLocalStorage()) || normalizeState(readCookie());
  }

  function getPreferences() {
    var state = getState();
    return state ? normalizePreferences(state.categories) : normalizePreferences();
  }

  function createState(preferences, source) {
    return {
      version: CONSENT_VERSION,
      categories: normalizePreferences(preferences),
      updatedAt: new Date().toISOString(),
      source: source || "ui"
    };
  }

  function persistState(state) {
    writeLocalStorage(state);
    writeCookie(state);
  }

  function hasConsent(category) {
    if (category === "necessary") {
      return true;
    }

    if (OPTIONAL_CATEGORIES.indexOf(category) === -1) {
      return false;
    }

    var state = getState();
    return Boolean(state && state.categories && state.categories[category]);
  }

  function granted(value) {
    return value ? "granted" : "denied";
  }

  function getGoogleConsentPayload(state) {
    var categories = state ? state.categories : {};
    var analytics = Boolean(categories.analytics);
    var marketing = Boolean(categories.marketing);
    var preferences = Boolean(categories.preferences);

    return {
      analytics_storage: granted(analytics),
      ad_storage: granted(marketing),
      ad_user_data: granted(marketing),
      ad_personalization: granted(marketing),
      functionality_storage: granted(preferences),
      personalization_storage: granted(preferences),
      security_storage: "granted"
    };
  }

  function ensureDataLayer() {
    var name = config.dataLayerName || "dataLayer";
    window[name] = window[name] || [];

    if (typeof window.gtag !== "function") {
      window.gtag = function () {
        window[name].push(arguments);
      };
    }
  }

  function applyGoogleConsent(command, state) {
    ensureDataLayer();

    var payload = getGoogleConsentPayload(state);
    if (command === "default") {
      payload.wait_for_update = 500;
    }

    window.gtag("consent", command, payload);
  }

  function shouldLoadGtm(state) {
    return Boolean(state && state.categories && (state.categories.analytics || state.categories.marketing));
  }

  function loadGtm() {
    var state = getState();
    var gtmId = String(config.gtmId || "").trim();

    if (!gtmId || integrations.gtm || !shouldLoadGtm(state)) {
      return false;
    }

    integrations.gtm = true;
    ensureDataLayer();

    var dataLayerName = config.dataLayerName || "dataLayer";
    window[dataLayerName].push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js"
    });

    var firstScript = document.getElementsByTagName("script")[0];
    var script = document.createElement("script");
    var dataLayerQuery = dataLayerName !== "dataLayer" ? "&l=" + encodeURIComponent(dataLayerName) : "";

    script.async = true;
    script.src = "https://www.googletagmanager.com/gtm.js?id=" + encodeURIComponent(gtmId) + dataLayerQuery;

    if (config.nonce) {
      script.setAttribute("nonce", config.nonce);
    }

    firstScript.parentNode.insertBefore(script, firstScript);
    return true;
  }

  function getDomainCandidates() {
    var host = window.location.hostname;
    var parts = host.split(".");

    if (parts.length < 2 || /^[\d.]+$/.test(host) || host === "localhost") {
      return [];
    }

    return ["." + parts.slice(-2).join("."), host];
  }

  function clearKnownOptionalCookies() {
    var cookieNames = document.cookie.split(";").map(function (cookie) {
      return cookie.split("=")[0].trim();
    }).filter(Boolean);

    var removablePrefixes = [
      "_ga",
      "_gid",
      "_gat",
      "_gac",
      "_gcl",
      "_fbp",
      "_fbc",
      "_clck",
      "_clsk",
      "MUID",
      "SRM_B",
      "ANONCHK",
      "SM"
    ];

    cookieNames.forEach(function (name) {
      var shouldRemove = removablePrefixes.some(function (prefix) {
        return name === prefix || name.indexOf(prefix + "_") === 0;
      });

      if (!shouldRemove) {
        return;
      }

      removeCookie(name);
      getDomainCandidates().forEach(function (domain) {
        removeCookie(name, domain);
      });
    });
  }

  function notify(state) {
    var event;

    listeners.slice().forEach(function (listener) {
      listener(state);
    });

    if (typeof window.CustomEvent === "function") {
      event = new window.CustomEvent("aiplaces:consentchange", {
        detail: {
          consent: state
        }
      });
      window.dispatchEvent(event);
    }
  }

  function ensureIntegrations() {
    var state = getState();

    applyGoogleConsent("update", state);

    if (!shouldLoadGtm(state)) {
      clearKnownOptionalCookies();
      return false;
    }

    return loadGtm();
  }

  function save(preferences, source) {
    var state = createState(preferences, source);

    persistState(state);
    ensureIntegrations();
    notify(state);

    return state;
  }

  function acceptAll(source) {
    return save({
      analytics: true,
      marketing: true,
      preferences: true
    }, source || "accept_all");
  }

  function rejectOptional(source) {
    return save({
      analytics: false,
      marketing: false,
      preferences: false
    }, source || "reject_optional");
  }

  function reset() {
    removeLocalStorage();
    clearConsentCookie();
    clearKnownOptionalCookies();
    applyGoogleConsent("default", null);
    notify(null);
  }

  function onChange(listener) {
    if (typeof listener !== "function") {
      return function () {};
    }

    listeners.push(listener);

    return function unsubscribe() {
      listeners = listeners.filter(function (candidate) {
        return candidate !== listener;
      });
    };
  }

  window.AiPlacesConsent = {
    version: CONSENT_VERSION,
    categories: ALL_CATEGORIES.slice(),
    optionalCategories: OPTIONAL_CATEGORIES.slice(),
    configure: function (nextConfig) {
      mergeConfig(nextConfig);
      applyGoogleConsent("default", getState());
      ensureIntegrations();
    },
    getState: getState,
    getPreferences: getPreferences,
    hasConsent: hasConsent,
    save: save,
    acceptAll: acceptAll,
    rejectOptional: rejectOptional,
    reset: reset,
    onChange: onChange,
    loadGtm: loadGtm,
    ensureIntegrations: ensureIntegrations
  };

  applyGoogleConsent("default", getState());
  ensureIntegrations();
})(window, document);
